import { del, get, patch, post, put } from '@eika-infrastruktur/request';

import { SearchParamsType } from '@components/app/routes';
import { SearchParams } from '@domain/app';
import { CardType, HardBlockReason } from '@generated-types';

export const isValidBooleanQuery = (value: string | null): value is 'true' | 'false' =>
    value !== null && (value === 'true' || value === 'false');

const getReasonSearchParams = (selectedReason: HardBlockReason): SearchParamsType => ({
    [SearchParams.REASON]: selectedReason,
});

const getReorderSearchParams = (value: boolean): SearchParamsType => ({
    [SearchParams.REORDER]: value.toString(),
});

const getChangedPinSearchParams = (value: boolean): SearchParamsType => ({
    [SearchParams.CHANGED_PIN]: value.toString(),
});

export const ensureStringIsReason = (potentialReasonKey: string | null | undefined): HardBlockReason | null => {
    if (typeof potentialReasonKey === 'undefined') {
        return null;
    }

    const found = Object.values(HardBlockReason).find((reasonKey) => reasonKey === potentialReasonKey);

    return found ?? null;
};

export const getQueryValue = (queryValue: string | string[] | null | undefined) => {
    if (typeof queryValue === 'string' && queryValue.length !== 0) {
        return queryValue;
    }

    return undefined;
};

export const getBooleanQueryValue = (value: 'true' | 'false'): boolean => value === 'true';

const createV3EndpointUrl = (baseUrl: string, urlSearchParams: URLSearchParams | undefined) =>
    `${baseUrl}${urlSearchParams ? `?${urlSearchParams.toString()}` : ''}`;

const appendCardTypeParam = (urlSearchParams: URLSearchParams, cardType: CardType) => {
    urlSearchParams.append('ctype', cardType.toString());
};

export const cardTypePost =
    (cardType: CardType) =>
    <R>(
        url: string,
        body?: unknown,
        urlParams: URLSearchParams = new URLSearchParams(),
        options: RequestInit = {},
    ): Promise<R> => {
        appendCardTypeParam(urlParams, cardType);

        return post<R>(createV3EndpointUrl(url, urlParams), body, options);
    };

export const cardTypeGet =
    (cardType: CardType, params: boolean = true) =>
    <R>(url: string, urlParams: URLSearchParams = new URLSearchParams(), options: RequestInit = {}): Promise<R> => {
        appendCardTypeParam(urlParams, cardType);

        return get<R>(createV3EndpointUrl(url, params ? urlParams : undefined), options);
    };

export const cardTypePut =
    (cardType: CardType) =>
    <R>(
        url: string,
        body?: unknown,
        urlParams: URLSearchParams = new URLSearchParams(),
        options: RequestInit = {},
    ): Promise<R> => {
        appendCardTypeParam(urlParams, cardType);

        return put<R>(createV3EndpointUrl(url, urlParams), body, options);
    };

export const cardTypeDel =
    (cardType: CardType) =>
    <R>(url: string, urlParams: URLSearchParams = new URLSearchParams(), options: RequestInit = {}): Promise<R> => {
        appendCardTypeParam(urlParams, cardType);

        return del<R>(createV3EndpointUrl(url, urlParams), options);
    };

export const cardTypePatch =
    (cardType: CardType) =>
    <R>(
        url: string,
        body?: unknown,
        urlParams: URLSearchParams = new URLSearchParams(),
        options: RequestInit = {},
    ): Promise<R> => {
        appendCardTypeParam(urlParams, cardType);

        return patch<R>(createV3EndpointUrl(url, urlParams), body, options);
    };

export const Query = {
    getReorderSearchParams,
    getChangedPinSearchParams,
    getReasonSearchParams,
};
